/**
 * ============================================================================
 * ServicePermissionPage — 역할×서비스(RPC) 실행 권한 관리
 * ============================================================================
 *
 * 【역할】
 *   백엔드 RPC 호출 가능 여부를 역할(Role) 수준에서 관리.
 *   서비스명(예: "customer/searchList") 단위로 canExecute 토글하여 인터셉터에서
 *   호출 차단 (403 forbidden). 메뉴 권한과 별개의 backend gate.
 *
 * 【주요 기능】
 *   - 역할 선택 Select → 해당 역할의 서비스 권한 자동 로드
 *   - 서비스명 입력: AutoComplete (등록된 서비스명 suggest, menuId 자동 채움)
 *   - 인라인 편집: canExecute 체크박스 + description 텍스트
 *   - 추가/삭제/벌크 저장 (DataSet C/U/D)
 *
 * 【Backend 호출】
 *   - POST /api/v1/transaction (service=adminRole/searchRoleList)            — 역할 목록
 *   - POST /api/v1/transaction (service=adminServicePerm/searchServiceNames) — 서비스명 풀
 *   - POST /api/v1/transaction (service=adminServicePerm/searchList)         — 권한 매트릭스
 *   - POST /api/v1/transaction (service=adminServicePerm/save)               — 권한 저장
 *
 * 【DataSet/Hook】
 *   - useDataSet<RoleRow>()    — roleDs : 역할 목록 (Select 옵션)
 *   - useDataSet<SvcPermRow>() — permDs : 역할×서비스 권한 (PK: roleId+serviceName)
 *   - useDataSet<SvcNameRow>() — nameDs : 서비스명 풀 (AutoComplete 옵션)
 *   - useTransaction()         — RPC 통합 호출
 *   - useLabel()               — i18n 라벨(t)
 *
 * 【사용처】 router/index.tsx — `{ path: '/admin/service-perm', element: <ServicePermissionPage /> }`
 * 【포팅 원본】 vue-spring-fw `frontend/src/pages/PageServicePermission.vue`
 * 【의존성】 antd AutoComplete/Checkbox/Input/Select/Space/Tag, framework hooks
 */
import { useEffect, useMemo, useState } from 'react'
import { AutoComplete, Checkbox, Input, Select, Space, Tag } from 'antd'
import CrudToolbar from '@/components/common/CrudToolbar'
import PageContainer from '@/components/common/PageContainer'
import DataTable from '@/components/common/DataTable'
import { useDataSet, useTransaction } from '@/framework'
import { useLabel } from '@/framework/useLabel'
import { makeColumns, type ColumnSpec } from '@/framework/grid/makeColumns'
import type { TrackedRow } from '@/framework/useDataSet'

/** 역할 1행 — Select 옵션. */
interface RoleRow {
  /** 역할 PK. */
  roleId: string
  /** 역할 표시명. */
  roleName: string
}
/** 서비스 권한 1행 — 역할×서비스 매핑. */
interface SvcPermRow {
  /** 역할 PK (FK). */
  roleId: string
  /** 서비스명 PK 일부 (예: "customer/searchList"). */
  serviceName: string
  /** 연결 메뉴 ID (선택, 서비스명 선택 시 자동 채움). */
  menuId?: string
  /** 실행 권한 (1=허용 / 0=차단, 인터셉터에서 403 발생). */
  canExecute: number
  /** 권한 설명/메모. */
  description?: string
}

/** 0/1/'Y'/true 등 truthy 표현 정규화. */
const isOn = (v: unknown) => v === 1 || v === '1' || v === true || v === 'Y'
/** 서비스명 풀 1행 — AutoComplete 옵션. */
interface SvcNameRow {
  /** 등록된 서비스명. */
  serviceName: string
  /** 해당 서비스가 호출되는 대표 메뉴 ID (선택 시 자동 채움). */
  menuId?: string
}

/**
 * ServicePermissionPage — 컴포넌트/함수 (자동 생성된 JSDoc 스텁).
 * @returns 렌더 결과 또는 반환값
 */
export default function ServicePermissionPage() {
  const roleDs = useDataSet<RoleRow>()
  const permDs = useDataSet<SvcPermRow>()
  const nameDs = useDataSet<SvcNameRow>()
  const { transaction } = useTransaction()
  const { t } = useLabel()
  const [roleId, setRoleId] = useState('')

  useEffect(() => {
    transaction({
      id: 'role_search',
      service: 'adminRole/searchRoleList',
      outds: { ds_role: roleDs }
    })
    transaction({
      id: 'svc_name_search',
      service: 'adminServicePerm/searchServiceNames',
      outds: { ds_serviceNameList: nameDs }
    })
  }, [])

  /**
   * 선택 역할의 서비스 권한 매트릭스 조회.
   * @param rid 외부 roleId (생략 시 state)
   * 미선택 시 무동작.
   */
  const searchPerms = (rid?: string) => {
    const r = rid ?? roleId
    if (!r) return
    return transaction({
      id: 'perm_search',
      service: 'adminServicePerm/searchList',
      inds: { ds_search: { roleId: r } },
      outds: { ds_servicePermList: permDs }
    })
  }

  /**
   * 서비스 권한 저장 — adminServicePerm/save.
   * 컨텍스트(roleId)는 ds_search 로, 변경분은 ds_servicePermList 로 전송.
   * 성공 시 매트릭스 재조회로 동기화.
   */
  const save = async () => {
    if (!roleId) return
    const res = await transaction({
      id: 'perm_save',
      service: 'adminServicePerm/save',
      action: 'save',
      inds: { ds_search: { roleId }, ds_servicePermList: permDs }
    })
    if (res.success) searchPerms()
  }

  /** 신규 권한 행 추가 — 역할 선택 후 빈 행 push (canExecute=1 기본). */
  const addRow = () => {
    if (!roleId) return
    permDs.addRow({ roleId, serviceName: '', menuId: '', canExecute: 1, description: '' })
  }
  /** 선택행 삭제 — DataSet C/U/D 추적. */
  const del = () => permDs.selectedIndex >= 0 && permDs.deleteRow(permDs.selectedIndex)

  /**
   * AutoComplete 값 변경 핸들러.
   * - 입력 v 가 nameDs 풀에 있으면 그 행의 menuId 도 함께 채움
   * - 자유 입력도 허용 (수동 등록 가능)
   */
  const onSvcNameChange = (idx: number, v: string) => {
    const found = nameDs.rows.find((r) => r.data.serviceName === v)
    permDs.updateRow(idx, { serviceName: v, menuId: found?.data.menuId ?? '' })
  }

  const columns = useMemo(() => {
    type SvcTRow = TrackedRow<SvcPermRow>
    const specs: ColumnSpec<SvcTRow>[] = [
      {
        key: 'COL_SERVICE_NAME',
        dataIndex: ['data', 'serviceName'],
        width: 320,
        render: (_v, r, i) => (
          <AutoComplete
            size="small"
            style={{ width: '100%' }}
            value={r.data.serviceName}
            options={nameDs.rows.map((x) => ({ value: x.data.serviceName }))}
            onChange={(v) => onSvcNameChange(i, v)}
            filterOption={(input, opt) =>
              (opt?.value ?? '').toLowerCase().includes(input.toLowerCase())
            }
          />
        )
      },
      { key: 'COL_MENU_ID', dataIndex: ['data', 'menuId'], width: 160 },
      {
        key: 'COL_CAN_EXECUTE',
        dataIndex: ['data', 'canExecute'],
        width: 100,
        align: 'center',
        render: (_v, r, i) => (
          <Checkbox
            checked={isOn(r.data.canExecute)}
            onChange={(e) => permDs.updateRow(i, { canExecute: e.target.checked ? 1 : 0 })}
          />
        )
      },
      {
        key: 'COL_DESCRIPTION',
        dataIndex: ['data', 'description'],
        render: (_v, r, i) => (
          <Input
            size="small"
            value={r.data.description}
            onChange={(e) => permDs.updateRow(i, { description: e.target.value })}
          />
        )
      },
      {
        key: 'COL_STATUS',
        dataIndex: '_state',
        width: 70,
        render: (s) =>
          s === 'C' ? (
            <Tag color="green">{t('TTL_ADD', '신규')}</Tag>
          ) : s === 'U' ? (
            <Tag color="orange">{t('TTL_EDIT', '수정')}</Tag>
          ) : null
      }
    ]
    return makeColumns(specs, t)
  }, [t, nameDs.rows, permDs.rows])

  return (
    <PageContainer>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Space>
          <span style={{ whiteSpace: 'nowrap' }}>{t('LBL_ROLE_COLON', '역할:')}</span>
          <Select
            style={{ width: 260 }}
            value={roleId || undefined}
            options={roleDs.rows.map((r) => ({
              label: `${r.data.roleName} (${r.data.roleId})`,
              value: r.data.roleId
            }))}
            onChange={(v) => {
              setRoleId(v)
              searchPerms(v)
            }}
            placeholder={t('PH_ROLE_SELECT', '역할 선택')}
          />
        </Space>
        <CrudToolbar
          onSearch={() => searchPerms()}
          onAdd={addRow}
          onDelete={del}
          onSave={save}
          dirty={permDs.isDirty}
        />
      </div>
      <DataTable<TrackedRow<SvcPermRow>>
        rowKey="_rowId"
        dataSource={permDs.getVisibleRows()}
        columns={columns}
        pagination={false}
        rowSelection={{
          type: 'radio',
          selectedRowKeys: permDs.selectedRow ? [permDs.selectedRow._rowId] : [],
          onChange: (keys) => {
            const idx = permDs.rows.findIndex((r) => r._rowId === keys[0])
            permDs.setSelectedIndex(idx)
          }
        }}
      />
    </PageContainer>
  )
}
